import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import './StudentLifeHouseSystemForm.css';

function StudentLifeHouseSystemForm({ apiBase }) {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [cmsData, setCmsData] = useState(null);
  const [formData, setFormData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [pendingFields, setPendingFields] = useState({});
  const [pendingHouses, setPendingHouses] = useState({});
  const [editingHouseIndex, setEditingHouseIndex] = useState(null);
  const [editingHouseData, setEditingHouseData] = useState(null);
  const [showHouseModal, setShowHouseModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const sectionRes = await fetch(`${apiBase}/studentlife/cms/sections?published=true`);
      const sections = await sectionRes.json();
      const houseSection = sections.find(s => s.section_type === 'student-life-house-system');
      
      if (!houseSection) {
        toast('House System section not found', 'error');
        setLoading(false);
        return;
      }

      setCmsData(houseSection);
      const content = JSON.parse(houseSection.content_published || '{}');
      const enData = content.en || content;
      setFormData(enData);
      setOriginalData(JSON.parse(JSON.stringify(enData)));

      const pendingRes = await fetch(`${apiBase}/cms/approvals/pending?section_id=${houseSection.id}`);
      const pendingList = await pendingRes.json();
      
      if (Array.isArray(pendingList)) {
        const pendingFieldsMap = {};
        const pendingHousesMap = {};
        
        pendingList.forEach(change => {
          if (['kicker', 'title', 'description'].includes(change.field_id)) {
            pendingFieldsMap[change.field_id] = true;
          } else if (change.field_id?.includes('houses.')) {
            const match = change.field_id.match(/houses\.(\d+)\./);
            if (match) pendingHousesMap[parseInt(match[1])] = true;
          }
        });
        
        setPendingFields(pendingFieldsMap);
        setPendingHouses(pendingHousesMap);
      }

      setLoading(false);
    } catch (err) {
      console.error('[StudentLifeHouseSystemForm] Load error:', err);
      toast('Failed to load house system data', 'error');
      setLoading(false);
    }
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleHouseFieldChange = (field, value) => {
    setEditingHouseData(prev => ({ ...prev, [field]: value }));
  };

  const handleHouseArrayChange = (arrayName, index, value) => {
    setEditingHouseData(prev => {
      const newArray = [...(prev[arrayName] || [])];
      newArray[index] = value;
      return { ...prev, [arrayName]: newArray };
    });
  };

  const handleAddArrayItem = (arrayName) => {
    setEditingHouseData(prev => ({
      ...prev,
      [arrayName]: [...(prev[arrayName] || []), '']
    }));
  };

  const handleRemoveArrayItem = (arrayName, index) => {
    setEditingHouseData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].filter((_, i) => i !== index)
    }));
  };

  const submitHeaderField = async (fieldName) => {
    if (JSON.stringify(formData[fieldName]) === JSON.stringify(originalData[fieldName])) {
      toast('No changes to submit', 'warning');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const requestBody = {
        section_id: cmsData.id,
        field_id: fieldName,
        field_name: fieldName.replace(/_/g, ' '),
        new_value: formData[fieldName],
        old_value: originalData[fieldName],
        locale: 'en'
      };

      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();

      if (result.success) {
        toast('Submitted for approval', 'success');
        setPendingFields(prev => ({ ...prev, [fieldName]: true }));
        setOriginalData(prev => ({ ...prev, [fieldName]: formData[fieldName] }));
      } else {
        toast(result.error || 'Submit failed', 'error');
      }
    } catch (err) {
      toast('Error: ' + err.message, 'error');
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const openHouseEditor = (index) => {
    const house = formData.houses[index];
    setEditingHouseIndex(index);
    setEditingHouseData(JSON.parse(JSON.stringify(house)));
    setShowHouseModal(true);
  };

  const closeHouseEditor = () => {
    setShowHouseModal(false);
    setEditingHouseIndex(null);
    setEditingHouseData(null);
  };

  const submitHouseChanges = async () => {
    if (!editingHouseData) return;

    if (JSON.stringify(editingHouseData) === JSON.stringify(originalData.houses[editingHouseIndex])) {
      toast('No changes to submit', 'warning');
      return;
    }

    const houseKey = `house_${editingHouseIndex}`;
    setSubmitting(prev => ({ ...prev, [houseKey]: true }));

    try {
      const fieldId = `houses.${editingHouseIndex}.update`;
      const fieldName = `Update House: ${editingHouseData.name}`;

      const requestBody = {
        section_id: cmsData.id,
        field_id: fieldId,
        field_name: fieldName,
        old_value: originalData.houses[editingHouseIndex],
        new_value: editingHouseData,
        locale: 'en'
      };

      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();

      if (result.success) {
        toast('House updated and submitted for approval', 'success');
        
        const newHouses = [...formData.houses];
        newHouses[editingHouseIndex] = editingHouseData;
        setFormData(prev => ({ ...prev, houses: newHouses }));

        const newOriginalHouses = [...originalData.houses];
        newOriginalHouses[editingHouseIndex] = editingHouseData;
        setOriginalData(prev => ({ ...prev, houses: newOriginalHouses }));

        setPendingHouses(prev => ({ ...prev, [editingHouseIndex]: true }));
        closeHouseEditor();
      } else {
        toast(result.error || 'Submit failed', 'error');
      }
    } catch (err) {
      toast('Error: ' + err.message, 'error');
    } finally {
      setSubmitting(prev => ({ ...prev, [houseKey]: false }));
    }
  };

  if (loading) {
    return <div className="form-loading">Loading House System data...</div>;
  }

  if (!formData) {
    return <div className="form-error">No data to display</div>;
  }

  return (
    <div className="student-life-house-system-form">
      <div className="form-container">
        <h2>House System Management</h2>

        <div className="form-section">
          <h3>Section Header</h3>

          <div className="form-group">
            <label>Kicker *</label>
            <input
              type="text"
              name="kicker"
              value={formData.kicker || ''}
              onChange={handleFieldChange}
              placeholder="e.g., House System"
            />
            <button
              className={`btn-submit ${pendingFields.kicker ? 'pending' : ''}`}
              onClick={() => submitHeaderField('kicker')}
              disabled={submitting.kicker || pendingFields.kicker}
            >
              {submitting.kicker ? 'Submitting...' : pendingFields.kicker ? 'Pending Approval' : 'Submit'}
            </button>
          </div>

          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title || ''}
              onChange={handleFieldChange}
              placeholder="e.g., House System"
            />
            <button
              className={`btn-submit ${pendingFields.title ? 'pending' : ''}`}
              onClick={() => submitHeaderField('title')}
              disabled={submitting.title || pendingFields.title}
            >
              {submitting.title ? 'Submitting...' : pendingFields.title ? 'Pending Approval' : 'Submit'}
            </button>
          </div>

          <div className="form-group">
            <label>Description *</label>
            <textarea
              name="description"
              value={formData.description || ''}
              onChange={handleFieldChange}
              rows={4}
              placeholder="House system description..."
            />
            <button
              className={`btn-submit ${pendingFields.description ? 'pending' : ''}`}
              onClick={() => submitHeaderField('description')}
              disabled={submitting.description || pendingFields.description}
            >
              {submitting.description ? 'Submitting...' : pendingFields.description ? 'Pending Approval' : 'Submit'}
            </button>
          </div>
        </div>

        <div className="form-section">
          <h3>Houses ({formData.houses?.length || 0})</h3>
          
          <div className="houses-grid">
            {formData.houses?.map((house, index) => (
              <div key={index} className={`house-card ${pendingHouses[index] ? 'pending' : ''}`}>
                <div className="house-card-header" style={{ backgroundColor: house.color }}>
                  <h4>{house.name}</h4>
                  <span className="house-motto">"{house.motto}"</span>
                </div>
                <div className="house-card-body">
                  <p className="house-leader">👤 {house.houseLeader}</p>
                  <p className="house-members">👥 {house.members}</p>
                  <p className="house-values">⭐ {house.values?.length || 0} Values</p>
                </div>
                <div className="house-card-footer">
                  <button
                    className={`btn-edit ${pendingHouses[index] ? 'pending' : ''}`}
                    onClick={() => openHouseEditor(index)}
                    disabled={pendingHouses[index]}
                  >
                    {pendingHouses[index] ? 'Pending' : 'Edit'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showHouseModal && editingHouseData && (
        <div className="modal-overlay" onClick={closeHouseEditor}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ backgroundColor: editingHouseData.color }}>
              <h3>Edit {editingHouseData.name}</h3>
              <button className="btn-close" onClick={closeHouseEditor}>✕</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>House Name</label>
                <input
                  type="text"
                  value={editingHouseData.name || ''}
                  onChange={e => handleHouseFieldChange('name', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Motto</label>
                <input
                  type="text"
                  value={editingHouseData.motto || ''}
                  onChange={e => handleHouseFieldChange('motto', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={editingHouseData.description || ''}
                  onChange={e => handleHouseFieldChange('description', e.target.value)}
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>House Leader</label>
                <input
                  type="text"
                  value={editingHouseData.houseLeader || ''}
                  onChange={e => handleHouseFieldChange('houseLeader', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Members Info</label>
                <input
                  type="text"
                  value={editingHouseData.members || ''}
                  onChange={e => handleHouseFieldChange('members', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Highlights</label>
                <textarea
                  value={editingHouseData.highlights || ''}
                  onChange={e => handleHouseFieldChange('highlights', e.target.value)}
                  rows={2}
                />
              </div>

              <div className="form-section-small">
                <h5>Core Values ({editingHouseData.values?.length || 0})</h5>
                {editingHouseData.values?.map((value, idx) => (
                  <div key={idx} className="array-item">
                    <input
                      type="text"
                      value={value}
                      onChange={e => handleHouseArrayChange('values', idx, e.target.value)}
                      placeholder={`Value ${idx + 1}`}
                    />
                    <button
                      className="btn-remove"
                      onClick={() => handleRemoveArrayItem('values', idx)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button className="btn-add-item" onClick={() => handleAddArrayItem('values')}>
                  + Add Value
                </button>
              </div>

              <div className="form-section-small">
                <h5>Activities ({editingHouseData.activities?.length || 0})</h5>
                {editingHouseData.activities?.map((activity, idx) => (
                  <div key={idx} className="array-item">
                    <input
                      type="text"
                      value={activity}
                      onChange={e => handleHouseArrayChange('activities', idx, e.target.value)}
                      placeholder={`Activity ${idx + 1}`}
                    />
                    <button
                      className="btn-remove"
                      onClick={() => handleRemoveArrayItem('activities', idx)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button className="btn-add-item" onClick={() => handleAddArrayItem('activities')}>
                  + Add Activity
                </button>
              </div>

              <div className="form-section-small">
                <h5>Achievements ({editingHouseData.achievements?.length || 0})</h5>
                {editingHouseData.achievements?.map((achievement, idx) => (
                  <div key={idx} className="array-item">
                    <input
                      type="text"
                      value={achievement}
                      onChange={e => handleHouseArrayChange('achievements', idx, e.target.value)}
                      placeholder={`Achievement ${idx + 1}`}
                    />
                    <button
                      className="btn-remove"
                      onClick={() => handleRemoveArrayItem('achievements', idx)}
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <button className="btn-add-item" onClick={() => handleAddArrayItem('achievements')}>
                  + Add Achievement
                </button>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={closeHouseEditor}>Cancel</button>
              <button
                className="btn-save"
                onClick={submitHouseChanges}
                disabled={submitting[`house_${editingHouseIndex}`]}
              >
                {submitting[`house_${editingHouseIndex}`] ? 'Submitting...' : 'Submit for Approval'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StudentLifeHouseSystemForm;
